import store from "@/store";
import eventBus from "@/utils/eventBus";
import { clientPointToCanvas } from "@/utils/utils";

const ctrlKey = 17,
  vKey = 86, // 粘贴
  cKey = 67, // 复制
  xKey = 88, // 剪切
  yKey = 89, // 重做
  zKey = 90, // 撤销
  gKey = 71, // 组合
  bKey = 66, // 拆分
  lKey = 76, // 锁定
  uKey = 85, // 解锁
  sKey = 83, // 保存
  pKey = 80, // 预览
  dKey = 68, // 删除
  deleteKey = 46, // 删除
  eKey = 69; // 清空画布

export const keycodes = [66, 67, 68, 69, 71, 76, 80, 83, 85, 86, 88, 89, 90];

// 与组件状态无关的操作
const basemap = {
  [vKey]: paste,
  [yKey]: redo,
  [zKey]: undo,
  [sKey]: save,
  [pKey]: preview,
  [eKey]: clearCanvas,
};

// 组件锁定状态下可以执行的操作
const lockMap = {
  ...basemap,
  [uKey]: unlock,
};

// 组件未锁定状态下可以执行的操作
const unlockMap = {
  ...basemap,
  [cKey]: copy,
  [xKey]: cut,
  [gKey]: compose,
  [bKey]: decompose,
  [dKey]: deleteComponent,
  [deleteKey]: deleteComponent,
  [lKey]: lock,
};

let lastPointerClientX = null;
let lastPointerClientY = null;
let pointerTracked = false;
let canvasKeyHandler = null;

export function isTypingTarget(e) {
  const t = e && e.target;
  if (!t) {
    return false;
  }
  const tag = (t.tagName || "").toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") {
    return true;
  }
  if (t.isContentEditable) {
    return true;
  }
  if (typeof t.closest === "function") {
    if (t.closest("textarea, input, select, [contenteditable='true']")) {
      return true;
    }
    if (t.closest(".el-input, .el-textarea, .el-select, .canvas-ai-panel, .canvas-ai-drawer")) {
      return true;
    }
  }
  return false;
}

export function isTextInputActive() {
  return isTypingTarget({ target: document.activeElement });
}

export function isCanvasPanHotkey(e) {
  if (isTypingTarget(e) || isTextInputActive()) {
    return false;
  }
  const t = e && e.target;
  if (t && typeof t.closest === "function") {
    if (t.closest(".canvas-ai-panel, .canvas-ai-drawer, .el-dialog, .el-message-box, .el-drawer")) {
      return false;
    }
  }
  return !!document.getElementById("editor");
}

function isCtrlLike(e) {
  return !!(e && (e.ctrlKey || e.metaKey));
}

function isCanvasHotkeyContext() {
  if (store.state && store.state.canvasEditorLeaving) {
    return false;
  }
  return !!document.getElementById("editor");
}

export function listenGlobalKeyDown() {
  if (!pointerTracked) {
    pointerTracked = true;
    window.addEventListener(
      "mousemove",
      (e) => {
        lastPointerClientX = e.clientX;
        lastPointerClientY = e.clientY;
      },
      true
    );
  }
  unlistenGlobalKeyDown();
  canvasKeyHandler = (e) => {
    if (!isCanvasHotkeyContext()) {
      return;
    }
    if (isTypingTarget(e) || isTextInputActive()) {
      return;
    }
    const { curComponent } = store.state;
    if (e.keyCode == deleteKey) {
      e.preventDefault();
      deleteComponent();
      return;
    }
    if (!isCtrlLike(e)) {
      return;
    }
    const locked = curComponent && curComponent.isLock;
    const handler = locked ? lockMap[e.keyCode] : unlockMap[e.keyCode];
    if (!handler) {
      return;
    }
    e.preventDefault();
    handler();
  };
  window.addEventListener("keydown", canvasKeyHandler);
}

export function unlistenGlobalKeyDown() {
  if (canvasKeyHandler) {
    window.removeEventListener("keydown", canvasKeyHandler);
    canvasKeyHandler = null;
  }
  if (window.onkeydown) {
    window.onkeydown = null;
  }
}

function isBatch() {
  return store.state.areaData && store.state.areaData.components && store.state.areaData.components.length
}

function copy() {
  store.commit("copy", isBatch());
}

function clampPastePoint(point) {
  const editor = document.getElementById("editor");
  let maxW = 1200;
  let maxH = 740;
  if (editor) {
    const w = parseFloat(editor.style.width);
    const h = parseFloat(editor.style.height);
    if (w > 0) maxW = w;
    if (h > 0) maxH = h;
  }
  return {
    left: Math.min(Math.max(0, Number(point.left) || 0), maxW),
    top: Math.min(Math.max(0, Number(point.top) || 0), maxH),
  };
}

function paste() {
  if (
    typeof lastPointerClientX === "number" &&
    typeof lastPointerClientY === "number"
  ) {
    const point = clampPastePoint(
      clientPointToCanvas(lastPointerClientX, lastPointerClientY)
    );
    store.commit("setPasteCanvasPoint", {
      left: point.left,
      top: point.top,
    });
    store.commit("paste", true);
  } else {
    store.commit("paste");
  }
  store.commit("recordSnapshot");
}

function cut() {
  store.commit("cut", isBatch());
  store.commit("recordSnapshot");
}

function redo() {
  store.commit("redo");
}

function undo() {
  store.commit("undo");
}

function compose() {
  if (store.state.areaData.components.length) {
    store.commit("compose");
    store.commit("recordSnapshot");
  }
}

function decompose() {
  const curComponent = store.state.curComponent;
  if (
    curComponent &&
    !curComponent.isLock &&
    curComponent.component == "Group"
  ) {
    store.commit("decompose");
    store.commit("recordSnapshot");
  }
}

function save() {
  eventBus.$emit("save");
}

function preview() {
  eventBus.$emit("preview");
}

function deleteComponent() {
  if (store.state.curComponent) {
    if (store.state.curComponent.component == "Back") return;
    store.commit("deleteComponent");
    store.commit("recordSnapshot");
  } else if(isBatch()) {
    store.commit("batchDeleteComponent", store.state.areaData.components || []);
    eventBus.$emit('hideArea');
    store.commit("recordSnapshot");
  }
}

function clearCanvas() {
  eventBus.$emit("clearCanvas");
}

function lock() {
  store.commit("lock");
}

function unlock() {
  store.commit("unlock");
}
